import { TASK_STATUS_LABELS, type TaskPriority, type TaskStatus } from '@erp/shared';
import clsx from 'clsx';
import { CalendarClock } from 'lucide-react';

/** Цвет статуса: работа — синий, проверка — сигнальный жёлтый, готово — зелёный. */
export const STATUS_TONE: Record<TaskStatus, { dot: string; badge: string }> = {
  new: { dot: 'bg-steel', badge: 'bg-sunken text-ink-soft' },
  in_progress: { dot: 'bg-link', badge: 'bg-[#e6eef7] text-link' },
  review: { dot: 'bg-signal', badge: 'bg-signal-soft text-[#7a5b00]' },
  done: { dot: 'bg-ok', badge: 'bg-ok-soft text-ok' },
  deferred: { dot: 'bg-line-strong', badge: 'bg-sunken text-steel' },
};

export function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[12px] font-medium',
        STATUS_TONE[status].badge,
      )}
    >
      <span aria-hidden className={clsx('size-1.5 rounded-full', STATUS_TONE[status].dot)} />
      {TASK_STATUS_LABELS[status]}
    </span>
  );
}

const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const timeFmt = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });

/** «сегодня 18:00», «завтра», «12 нояб.»; просроченный срок — красным. */
export function formatDeadline(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const startOf = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((startOf(d) - startOf(today)) / 86_400_000);
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  const time = hasTime ? ` ${timeFmt.format(d)}` : '';
  if (days === 0) return `сегодня${time}`;
  if (days === 1) return `завтра${time}`;
  if (days === -1) return `вчера${time}`;
  return `${dayFmt.format(d)}${d.getFullYear() !== today.getFullYear() ? ` ${d.getFullYear()}` : ''}${time}`;
}

export function Deadline({
  iso,
  overdue,
  compact,
}: {
  iso: string | null;
  overdue?: boolean;
  compact?: boolean;
}) {
  if (!iso) return compact ? null : <span className="text-steel">без срока</span>;
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 whitespace-nowrap tabular-nums',
        overdue ? 'font-medium text-danger' : 'text-ink-soft',
        compact && 'text-[12.5px]',
      )}
      title={overdue ? 'Срок прошёл' : undefined}
    >
      <CalendarClock className="size-3.5 shrink-0" aria-hidden />
      {formatDeadline(iso)}
    </span>
  );
}

export function PriorityMark({ priority }: { priority: TaskPriority }) {
  if (priority !== 'high') return null;
  return (
    <span className="inline-flex items-center rounded bg-danger-soft px-1.5 py-0.5 text-[11.5px] font-medium text-danger">
      важная
    </span>
  );
}

/** ISO → значение для <input type="datetime-local"> в локальном времени. */
export function toLocalInput(iso: string | null | undefined) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
